import cx from 'classnames'
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  type SanityFilter,
  type SanitySort,
} from '@data/sanity/queries/types/cart'
import { type SanityCollectionStrings } from '@data/sanity/queries/types/modules'
import { type SanityProductFragment } from '@data/sanity/queries/types/product'
import { clampRange, parseOptionalParameter } from '@lib/helpers'
import { type UrlParameter, usePrevious, useUrlParameters } from '@lib/hooks'
import { getSortedProducts } from '@lib/product'
import {
  getFilterFromUrlParameter,
  getSortParameterValue,
  getUpdatedParameter,
  useFilterAndSort,
} from '@lib/product-collection'
import { SiteContext } from '@lib/site-context'
import { StringsContext } from '@lib/strings-context'

import Button, { ButtonVariant } from '@components/buttons/button'
import ProductCard from '@blocks/shop/product-card'
import CollectionFilter from './filter'
import CollectionFilterChips from './filter-chips'
import CollectionSort from './sort'
import ComplexPortableText from '@components/complex-portable-text'

interface CollectionProps {
  collectionStrings: SanityCollectionStrings
  products: SanityProductFragment[]
  productIds?: string[]
  featuredProductIds: number[]
  paginationLimit: number
  title?: string
  sort?: SanitySort
  filter?: SanityFilter
}

const Collection = ({
  collectionStrings,
  products,
  productIds,
  featuredProductIds,
  paginationLimit,
  title,
  sort,
  filter,
}: CollectionProps) => {
  const { isRouteChanging } = useContext(SiteContext)
  const strings = useContext(StringsContext)

  const parsedProducts = useMemo(() => {
    const sortedProducts = productIds
      ? getSortedProducts(products, productIds)
      : products
    return sortedProducts
  }, [productIds, products])

  const [hasPagination, setHasPagination] = useState(
    paginationLimit > 0 && parsedProducts.length > paginationLimit,
  )
  const [currentCount, setCurrentCount] = useState(
    hasPagination ? paginationLimit : parsedProducts.length,
  )

  const productCardRef = useRef<(HTMLDivElement | null)[]>([])
  const filterGroups = filter?.groups ?? []

  // Manage URL parameters
  const defaultSortOption = sort?.options?.[0]?.type ?? null
  const [currentParameters, setCurrentParameters] = useUrlParameters([
    {
      name: 'page',
    },
    {
      name: 'sort',
      value: defaultSortOption,
    },
    ...filterGroups.map((filterGroup) => ({
      name: filterGroup.slug.current,
    })),
  ])
  const previousParameters = usePrevious(currentParameters)
  const activeParameters =
    isRouteChanging && previousParameters
      ? previousParameters
      : currentParameters

  // Manage sorting value
  const activeSortValue = getSortParameterValue(activeParameters)

  // Manage filter values
  const activeFilters = activeParameters
    .filter(
      (activeParameter) => !['page', 'sort'].includes(activeParameter.name),
    )
    .map(getFilterFromUrlParameter(filterGroups))
  const activeFilterValueCount = activeFilters.reduce(
    (total, { values }) => total + values.length,
    0,
  )

  // Manage sorting and pagination
  const processedProducts = useFilterAndSort(
    parsedProducts,
    featuredProductIds,
    activeFilters,
    // Use default sort option as fallback, since when sort option value matches the default it's empty
    activeSortValue ?? defaultSortOption,
  )
  const paginatedProducts = [...processedProducts.slice(0, currentCount)]

  // Manage product pagination
  useEffect(() => {
    const pageParameter = activeParameters.find(
      (activeParameter) => activeParameter.name === 'page',
    )
    const page = Number(parseOptionalParameter(pageParameter?.value) ?? 1)
    const productCount = processedProducts.length
    const pageCount = Math.ceil(productCount / paginationLimit)

    if (page <= 1 || page > pageCount) {
      return
    }

    const newCount = clampRange(paginationLimit * page, 1, productCount)

    if (newCount !== currentCount) {
      setCurrentCount(newCount)
    }
  }, [currentCount, processedProducts, activeParameters, paginationLimit])

  // Focus on the first link in the first or last product card
  useEffect(() => {
    const pageProductIndex =
      currentCount < processedProducts.length
        ? currentCount - paginationLimit
        : processedProducts.length - 1
    productCardRef.current[pageProductIndex]
      ?.querySelector<HTMLAnchorElement>('[href]')
      ?.focus({
        preventScroll: true,
      })
  }, [currentCount, paginationLimit, processedProducts.length])

  // Update pagination status
  useEffect(
    () => setHasPagination(currentCount < processedProducts.length),
    [currentCount, processedProducts.length],
  )

  // Handle filter and sort updates
  const updateParameters = useCallback(
    (newParameters: UrlParameter[]) =>
      setCurrentParameters(
        activeParameters.map(getUpdatedParameter(newParameters)),
      ),
    [activeParameters, setCurrentParameters],
  )

  // Handle loading more products
  const loadMore = useCallback(() => {
    const newCount = clampRange(
      currentCount + paginationLimit,
      1,
      processedProducts.length,
    )

    const newPage = Math.ceil(newCount / paginationLimit)

    setCurrentCount(newCount)
    updateParameters([
      {
        name: 'page',
        value: newPage > 1 ? `${newPage}` : null,
      },
    ])
  }, [currentCount, processedProducts, paginationLimit, updateParameters])

  const clearFilters = () =>
    updateParameters(
      activeFilters.map((activeFilter) => ({
        name: activeFilter.name,
        value: null,
      })),
    )

  if (parsedProducts.length === 0) {
    return null
  }

  return (
    <section>
      <div className="sticky top-[var(--headerHeight)] z-10 bg-pageBG bg-opacity-95">
        <div className="container flex justify-between">
          {filter?.isActive && (
            <CollectionFilter
              collectionStrings={collectionStrings}
              filterGroups={filterGroups}
              activeFilters={activeFilters}
              activeFilterValueCount={activeFilterValueCount}
              itemTotal={processedProducts.length}
              onChange={updateParameters}
            />
          )}

          {sort?.isActive && (
            <CollectionSort
              collectionStrings={collectionStrings}
              sortOptions={sort.options ?? []}
              activeSortValue={activeSortValue}
              onChange={updateParameters}
            />
          )}
        </div>
      </div>

      {filter?.isActive && (
        <div className="bg-pageText">
          <div className="container">
            <CollectionFilterChips
              collectionStrings={collectionStrings}
              filterGroups={filterGroups}
              activeFilters={activeFilters}
              activeFilterValueCount={activeFilterValueCount}
              onClick={updateParameters}
            />
          </div>
        </div>
      )}

      <div className="container relative z-0 overflow-hidden">
        <div
          className={cx('grid grid-cols-1 md:grid-cols-3 gap-x-10 -m-px', {
            'grid-cols-1': processedProducts.length === 0,
          })}
        >
          {paginatedProducts.map((product, index) => (
            <ProductCard
              key={
                product.productID +
                activeParameters
                  .filter((activeParam) => activeParam.name !== 'page')
                  .map((activeParam) => activeParam.value)
                  .filter(Boolean)
                  .join('-')
              }
              ref={(node) => {
                productCardRef.current[index] = node
              }}
              product={product}
              activeFilters={activeFilters}
              hasVisuals={!!product.galleryPhotos || !!product.listingPhotos}
              showGallery={
                product.useGallery === 'true' && !!product.galleryPhotos
              }
              showThumbnails={
                product.useGallery !== 'true' && !!product.listingPhotos
              }
              showOption={!!product.surfaceOption}
              showPrice
              showQuickAdd
              isInline
            />
          ))}
        </div>

        {processedProducts.length === 0 && (
          <div className="px-8 py-16 min-h-[35vh] text-center">
            <ComplexPortableText
              content={collectionStrings.collectionFilterNoResults}
            />
            <Button
              variant={ButtonVariant.OUTLINED}
              onClick={clearFilters}
              className="mt-8"
            >
              {collectionStrings.collectionClearFiltersLabel}
            </Button>
          </div>
        )}

        {hasPagination && (
          <div className="relative py-4 sm:py-8 text-center">
            <Button variant={ButtonVariant.FILLED} onClick={loadMore}>
              {strings.buttonLoadMore}
              <span className="sr-only">
                {collectionStrings.collectionProducts?.replace(
                  /{title}/gi,
                  title ? `&quot;${title}&quot;` : '',
                )}
              </span>
            </Button>
          </div>
        )}

        {processedProducts.length > 0 && (
          <div className="relative py-4 sm:py-8 text-center">
            <p aria-live="polite" role="status" aria-atomic="true">
              {collectionStrings.collectionProductCount
                ?.replace(/{count}/gi, `${paginatedProducts.length}`)
                ?.replace(/{total}/gi, `${processedProducts.length}`)}
            </p>
          </div>
        )}
      </div>
    </section>
  )
}

export default Collection
